import { useMemo, useState } from 'react';
import { Button, Switch, useDisclosure } from '@heroui/react';
import type { ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/common/PageHeader';
import { Pagination } from '@/components/common/Pagination';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable, type ColumnMetaExt } from '@/components/common/DataTable';
import { SearchInput } from '@/components/common/SearchInput';
import { useTableQueryState } from '@/hooks/useTableQueryState';
import { useAliasesList, useUpdateAlias, useDeleteAlias } from '@/features/aliases/hooks';
import { AliasFormModal } from '@/features/aliases/components/AliasFormModal';
import type { Alias } from '@/features/aliases/types';
import { PlusIcon, PencilSquareIcon, TrashIcon, ArrowsRightLeftIcon } from '@/icons';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from '@heroui/react';
import { EllipsisVerticalIcon } from '@/icons';
import { useOpenOnNewParam } from '@/hooks/useOpenOnNewParam';
import { usePermission } from '@/hooks/usePermission';

export function AliasesPage() {
  const { state, setPage, setPageSize, setSearch, toggleSort } = useTableQueryState();

  const { data, isLoading, isError, refetch } = useAliasesList({ page: state.page, pageSize: state.pageSize, q: state.q || undefined, sort: state.sort || undefined });
  const form = useDisclosure();
  const [target, setTarget] = useState<Alias | null>(null);
  // Create/edit/delete need mailbox:manage at the gateway.
  const canManage = usePermission().can('mailbox:manage');
  useOpenOnNewParam(() => { if (canManage) { setTarget(null); form.onOpen(); } });
  const [toDelete, setToDelete] = useState<Alias | null>(null);
  const update = useUpdateAlias();
  const del = useDeleteAlias();

  const columns = useMemo<ColumnDef<Alias, unknown>[]>(() => [
    { id: 'address', header: 'Alias', meta: { sortKey: 'address' } as ColumnMetaExt,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <ArrowsRightLeftIcon className="h-4 w-4 text-fg-subtle" />
          <span className="font-medium text-fg">{row.original.address}</span>
        </div>
      ) },
    { id: 'target', header: 'Delivers to', cell: ({ row }) => <span className="text-sm text-fg-muted">{row.original.target}</span> },
    { id: 'enabled', header: 'Enabled', cell: ({ row }) => (
      <Switch size="sm" isSelected={row.original.enabled} aria-label="Toggle alias"
        onValueChange={(v) => update.mutate({ id: row.original.id, body: { enabled: v } })} />
    ) },
    { id: 'actions', header: '', meta: { className: 'text-right w-12' } as ColumnMetaExt, cell: ({ row }) => (
      <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
        <Dropdown placement="bottom-end">
          <DropdownTrigger><Button isIconOnly size="sm" variant="light" aria-label="Alias actions"><EllipsisVerticalIcon className="h-5 w-5 text-fg-muted" /></Button></DropdownTrigger>
          <DropdownMenu aria-label="Alias actions">
            {[<DropdownItem key="edit" startContent={<PencilSquareIcon className="h-4 w-4" />} onPress={() => { setTarget(row.original); form.onOpen(); }}>Edit</DropdownItem>,
            ...(canManage ? [<DropdownItem key="delete" className="text-danger" color="danger" startContent={<TrashIcon className="h-4 w-4" />} onPress={() => setToDelete(row.original)}>Delete</DropdownItem>] : [])]}
          </DropdownMenu>
        </Dropdown>
      </div>
    ) },
  ], [update, form, canManage]);

  return (
    <div>
      <PageHeader title="Aliases" description="Redirect an address to a real mailbox."
        actions={canManage ? <Button color="primary" startContent={<PlusIcon className="h-4 w-4" />} onPress={() => { setTarget(null); form.onOpen(); }}>Create alias</Button> : undefined} />
      <div className="rounded-xl border border-border bg-surface">
        <div className="border-b border-border p-4">
          <SearchInput value={state.q} onSearch={setSearch} placeholder="Search aliases…" className="sm:max-w-xs" ariaLabel="Search aliases" />
        </div>
        <DataTable<Alias> columns={columns} data={data?.data ?? []} getRowId={(a) => a.id} isLoading={isLoading} isError={isError} onRetry={refetch} sort={state.sort} onSort={toggleSort} emptyMessage="No aliases yet." />
        <Pagination page={state.page} pageSize={state.pageSize} total={data?.total} onPageChange={setPage} onPageSizeChange={setPageSize} />
      </div>
      <AliasFormModal isOpen={form.isOpen} onClose={form.onClose} alias={target} />
      <ConfirmDialog isOpen={!!toDelete} onClose={() => setToDelete(null)} onConfirm={async () => { if (toDelete) await del.mutateAsync(toDelete.id); setToDelete(null); }}
        title="Delete alias?" body={`Delete the alias ${toDelete?.address}?`} confirmLabel="Delete" tone="danger" isLoading={del.isPending} />
    </div>
  );
}
